import { vehiclesSlice } from "./vehiclesSlice";
import * as defaultActions from "../../../../../redux/_common";
import { Dispatch } from "redux";
import { constructInitialFilters } from "../../../../components/common/filters/utils";
import { get } from "../../../../../api/requester";
import { AppDispatch } from "../../../../../redux/store";
import {
  IAssignmentFormItem,
  IComplexSearchFilterItem,
  IAssignmentSource,
  IAuthorizationFormUser,
  IAuthorizationSource,
  IEditItem,
  IFilterParams,
  IInitialAssignment,
  IInitialAuthorization,
  ISelectOption,
  IValues,
  IVehicleTypeResolvedDetails,
} from "./_interfaces";
import * as fields from "../../pages/vehicles/edit/constants";
import { actions as categoryActions } from "../../../DataConfiguration/_redux/vehicleCategories";
import { actions as subCategoryActions } from "../../../DataConfiguration/_redux/vehicleSubcategories";
import { actions as manufacturerActions } from "../../../DataConfiguration/_redux/vehicleManufacturers";
import { actions as modelActions } from "../../../DataConfiguration/_redux/vehicleModels";

const { actions } = vehiclesSlice;
export const moduleUrl = "/api/appmasterdata/vehicles";

export const setValidationErrors = actions.setValidationErrors;

export const getVehicles = (params: IFilterParams) => defaultActions.getItems(moduleUrl, actions, params);
export const getVehicle = (id: string) => defaultActions.getItem(`${moduleUrl}/${id}`, actions, refineDataForTabs);
export const saveVehicle = (vehicle: IValues, id: string | null) => defaultActions.saveItem(moduleUrl, actions, vehicle, id, refineDataForSaving);
export const deleteVehicle = (id: string) => defaultActions.deleteItem(`${moduleUrl}/${id}`, actions);
export const getDropDownItems = (params: IFilterParams) => defaultActions.getDropDownItems("/api/appmasterdata/vehicles/status/select", actions, params);
export const getAccessSelectDropDownItems = (params: IFilterParams) =>
  defaultActions.getDropDownItems("/api/appmasterdata/vehicles/access/select", actions, params);
export const clearAll = () => defaultActions.clearAll(actions);
export const clearVehicle = () => defaultActions.clearItem(actions);
export const clearErrors = () => defaultActions.clearErrors(actions);

interface IDateTimeParts {
  year: number;
  month: number;
  day: number;
  hours: number;
  minutes: number;
  seconds: number;
}

interface ICreateVehicleAssignment {
  userId: string;
  startDate: string | null;
  endDate: string | null;
}

interface IDeleteById {
  id: string;
}

interface IVehicleSavePayload {
  id?: string;
  vehicleTypeId: string | null;
  vehicleStatusId: string | null;
  registrationNumber: string;
  createVehicleAuthorizations: Array<{ userId: string }>;
  deleteVehicleAuthorizations: Array<{ id: string }>;
  createVehicleAssignments: ICreateVehicleAssignment[];
  deleteVehicleAssignments: IDeleteById[];
}

type IInitialAssignmentLoose = {
  id?: string | null;
  userId?: string | null;
  startDate?: string | null;
  endDate?: string | null;
};

const getDateTimeParts = (date: Date): IDateTimeParts => ({
  year: date.getFullYear(),
  month: date.getMonth() + 1,
  day: date.getDate(),
  hours: date.getHours(),
  minutes: date.getMinutes(),
  seconds: date.getSeconds(),
});

const parseDateTimeParts = (value?: string | null): IDateTimeParts | null => {
  if (!value || typeof value !== "string") {
    return null;
  }

  const normalizedValue = value.trim();
  if (!normalizedValue) {
    return null;
  }

  // For date-only values (YYYY-MM-DD), parse as local date to avoid timezone shifting.
  if (!normalizedValue.includes("T") && !normalizedValue.includes(" ")) {
    const [rawYear, rawMonth, rawDay] = normalizedValue.split("-");
    const year = Number.parseInt(rawYear, 10);
    const month = Number.parseInt(rawMonth, 10);
    const day = Number.parseInt(rawDay, 10);
    const dateOnly = new Date(year, month - 1, day);

    if (
      Number.isNaN(dateOnly.getTime()) ||
      dateOnly.getFullYear() !== year ||
      dateOnly.getMonth() !== month - 1 ||
      dateOnly.getDate() !== day
    ) {
      return null;
    }

    return getDateTimeParts(dateOnly);
  }

  const parsed = new Date(normalizedValue);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return getDateTimeParts(parsed);
};

const formatTimeParts = ({ hours, minutes, seconds }: Pick<IDateTimeParts, "hours" | "minutes" | "seconds">) => {
  const normalizedHours = `${hours}`.padStart(2, "0");
  const normalizedMinutes = `${minutes}`.padStart(2, "0");
  const normalizedSeconds = `${seconds}`.padStart(2, "0");

  return `${normalizedHours}:${normalizedMinutes}:${normalizedSeconds}`;
};

const parseDateForPicker = (value?: string | null) => {
  if (!value) {
    return null;
  }

  const extracted = parseDateTimeParts(value);
  if (extracted) {
    return new Date(extracted.year, extracted.month - 1, extracted.day);
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
};

const parseTimeForPicker = (value?: string | null) => {
  if (!value) {
    return null;
  }

  const extracted = parseDateTimeParts(value);
  if (extracted) {
    return formatTimeParts(extracted);
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return formatTimeParts(getDateTimeParts(parsed));
};

const pickAuthorizationDisplayName = (item: IAuthorizationSource) => item.name || item.userName || item.fullName || item.authorizedUser || "";
const pickAssignmentDisplayName = (item: IAssignmentSource) => item.name || item.userName || item.fullName || item.assignedUser || "";

const pickAssignmentStart = (assignment: IAssignmentSource) => assignment.startDate || assignment.assignmentStartDate || null;
const pickAssignmentEnd = (assignment: IAssignmentSource) => assignment.endDate || assignment.assignmentEndDate || null;

const mapAuthorizationsForForm = (authorizations: IAuthorizationSource[] = []): IAuthorizationFormUser[] => {
  return (authorizations || []).reduce<IAuthorizationFormUser[]>((acc, authorization) => {
    const authorizationId = authorization.id || null;
    const userId = authorization.userId || authorization.driverId || authorizationId;

    if (!userId) {
      return acc;
    }

    acc.push({
      id: userId,
      authorizationId,
      name: pickAuthorizationDisplayName(authorization),
    });

    return acc;
  }, []);
};

const mapAssignmentsForForm = (assignments: IAssignmentSource[] = []): IAssignmentFormItem[] => {
  return (assignments || []).reduce<IAssignmentFormItem[]>((acc, assignment) => {
    const driverId = assignment.userId || assignment.driverId || assignment.id;

    if (!driverId) {
      return acc;
    }

    const assignmentStartDate = pickAssignmentStart(assignment);
    const assignmentEndDate = pickAssignmentEnd(assignment);

    acc.push({
      id: assignment.id || `${driverId}-${assignmentStartDate || ""}-${assignmentEndDate || ""}`,
      driverId,
      userId: assignment.userId,
      name: pickAssignmentDisplayName(assignment),
      startDate: parseDateForPicker(assignmentStartDate),
      startTime: parseTimeForPicker(assignmentStartDate),
      endDate: parseDateForPicker(assignmentEndDate),
      endTime: parseTimeForPicker(assignmentEndDate),
    });

    return acc;
  }, []);
};

const mapInitialAuthorizations = (authorizations: IAuthorizationSource[]): IInitialAuthorization[] => {
  return (authorizations || []).reduce<IInitialAuthorization[]>((acc, authorization) => {
    const authorizationId = authorization.id || null;
    const userId = authorization.userId || authorization.driverId || authorizationId;

    if (!userId) {
      return acc;
    }

    acc.push({
      id: authorizationId,
      userId,
    });

    return acc;
  }, []);
};

const mapInitialAssignments = (assignments: IAssignmentSource[]): IInitialAssignment[] => {
  return (assignments || [])
    .map((assignment) => {
      const assignmentId = assignment.id || null;
      const userId = assignment.userId || assignment.driverId || null;

      if (!assignmentId || !userId) {
        return null;
      }

      return {
        id: assignmentId,
        userId,
        startDate: pickAssignmentStart(assignment),
        endDate: pickAssignmentEnd(assignment),
      };
    })
    .filter((x): x is IInitialAssignment => Boolean(x));
};

type ISelectLoadAction = (params: IFilterParams) => unknown;

const getNameByIdFromSelect = async (dispatch: AppDispatch, loadAction: ISelectLoadAction, id: string | null | undefined) => {
  if (!id) {
    return null;
  }

  const response = await dispatch(loadAction({}) as never);
  const options = Array.isArray(response) ? (response as ISelectOption[]) : [];
  const selectedOption = options.find((option) => option?.id === id);

  return selectedOption?.name ?? null;
};

export const getVehicleTypeDetailsResolved =
  (vehicleTypeId: string) =>
  async (dispatch: AppDispatch): Promise<IVehicleTypeResolvedDetails> => {
    const vehicleType = await get(`/api/appmasterdata/vehicleTypes/${vehicleTypeId}`);

    const [categoryName, subCategoryName, manufacturerName, modelName] = await Promise.all([
      getNameByIdFromSelect(dispatch, categoryActions.getDropDownItems, vehicleType?.categoryId),
      getNameByIdFromSelect(dispatch, subCategoryActions.getDropDownItems, vehicleType?.subCategoryId),
      getNameByIdFromSelect(dispatch, manufacturerActions.getDropDownItems, vehicleType?.manufacturerId),
      getNameByIdFromSelect(dispatch, modelActions.getDropDownItems, vehicleType?.modelId),
    ]);

    return {
      category: categoryName,
      subCategory: subCategoryName,
      manufacturer: manufacturerName,
      model: modelName,
      maxPayload: vehicleType?.maxPayload ?? null,
      maxVolume: vehicleType?.maxVolume ?? null,
      interiorLength: vehicleType?.interiorLength ?? null,
      interiorWidth: vehicleType?.interiorWidth ?? null,
      interiorHeight: vehicleType?.interiorHeight ?? null,
    };
  };

const refineDataForTabs = (data: IEditItem) => {
  const details = data.details || data.type || {};
  const sourceAuthorizations = data.vehicleAuthorizations || data.authorizations || data.authorizedUsers || [];
  const sourceAssignments = data.vehicleAssignments || data.assignments || [];

  return {
    id: data.id,
    [fields.GENERAL]: {
      [fields.GENERAL_REGISTRATION_NUMBER]: data.registrationNumber,
      [fields.GENERAL_VEHICLE_TYPE_ID]: data.vehicleTypeId,
      [fields.GENERAL_LIFECYCLE_STATUS]: data.lifecycleStatus ?? data.vehicleStatusId ?? null,
    },
    [fields.DETAILS]: {
      [fields.DETAILS_CATEGORY]: details?.[fields.DETAILS_CATEGORY] ?? null,
      [fields.DETAILS_SUB_CATEGORY]: details?.[fields.DETAILS_SUB_CATEGORY] ?? null,
      [fields.DETAILS_MANUFACTURER]: details?.[fields.DETAILS_MANUFACTURER] ?? null,
      [fields.DETAILS_MODEL]: details?.[fields.DETAILS_MODEL] ?? null,
      [fields.DETAILS_MAX_PAYLOAD]: details?.[fields.DETAILS_MAX_PAYLOAD] ?? null,
      [fields.DETAILS_MAX_VOLUME]: details?.[fields.DETAILS_MAX_VOLUME] ?? null,
      [fields.DETAILS_INTERIOR_LENGTH]: details?.[fields.DETAILS_INTERIOR_LENGTH] ?? null,
      [fields.DETAILS_INTERIOR_WIDTH]: details?.[fields.DETAILS_INTERIOR_WIDTH] ?? null,
      [fields.DETAILS_INTERIOR_HEIGHT]: details?.[fields.DETAILS_INTERIOR_HEIGHT] ?? null,
    },
    [fields.DRIVER_ASSIGNMENTS]: {
      authorizedUsers: mapAuthorizationsForForm(sourceAuthorizations),
      assignments: mapAssignmentsForForm(sourceAssignments),
      initialAuthorizations: mapInitialAuthorizations(sourceAuthorizations),
      initialAssignments: mapInitialAssignments(sourceAssignments),
      assignmentModal: {
        driverId: null,
        startDate: null,
        startTime: null,
        endDate: null,
        endTime: null,
      },
    },
  };
};

const isNonEmptyString = (value: unknown): value is string => typeof value === "string" && value.length > 0;

const normalizeId = (value: unknown): string | null => {
  if (value === null || value === undefined) {
    return null;
  }

  const normalized = `${value}`.trim();
  return normalized.length > 0 ? normalized : null;
};

const toUtcIso = (year: number, month: number, day: number, hours: number, minutes: number, seconds: number) =>
  new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds, 0)).toISOString();

const combineDateAndTimeToIso = (dateValue: Date | string | null | undefined, timeValue: string | null | undefined) => {
  if (!dateValue) {
    return null;
  }

  let year: number;
  let month: number;
  let day: number;

  if (typeof dateValue === "string") {
    const extracted = parseDateTimeParts(dateValue);
    if (extracted) {
      year = extracted.year;
      month = extracted.month;
      day = extracted.day;
    } else {
      const parsed = new Date(dateValue);
      if (Number.isNaN(parsed.getTime())) {
        return null;
      }
      year = parsed.getFullYear();
      month = parsed.getMonth() + 1;
      day = parsed.getDate();
    }
  } else {
    const parsed = new Date(dateValue);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }
    year = parsed.getFullYear();
    month = parsed.getMonth() + 1;
    day = parsed.getDate();
  }

  let hours = 0;
  let minutes = 0;
  let seconds = 0;

  if (timeValue) {
    const [parsedHours = "0", parsedMinutes = "0", parsedSeconds = "0"] = timeValue.split(":");
    hours = Number(parsedHours);
    minutes = Number(parsedMinutes);
    seconds = Number(parsedSeconds);
  }

  return toUtcIso(year, month, day, hours, minutes, seconds);
};

const normalizeIncomingAssignmentDateToIso = (value: string | null | undefined) => {
  if (!value) {
    return null;
  }

  const extracted = parseDateTimeParts(value);
  if (extracted) {
    return toUtcIso(extracted.year, extracted.month, extracted.day, extracted.hours, extracted.minutes, extracted.seconds);
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed.toISOString();
};

const mapAuthorizedUserToUserId = (user: IAuthorizationFormUser | string) => {
  if (typeof user === "string") {
    return user;
  }
  return user?.id || user?.value || user?.userId;
};

const buildAuthorizationDiff = (driverAssignments: IValues["driverAssignments"]) => {
  const authorizedUsers = driverAssignments.authorizedUsers || [];
  const currentAuthorizedUserIds: string[] = Array.from(
    new Set((authorizedUsers || []).map(mapAuthorizedUserToUserId).filter(isNonEmptyString)),
  );

  const initialAuthorizations = (driverAssignments.initialAuthorizations || []) as Array<Partial<IInitialAuthorization>>;
  const initialAuthorizationsByUserId = new Map((initialAuthorizations || []).filter((x) => x?.userId).map((x) => [x.userId as string, x]));

  const createVehicleAuthorizations = currentAuthorizedUserIds
    .filter((userId) => !initialAuthorizationsByUserId.has(userId))
    .map((userId) => ({ userId }));

  const deleteVehicleAuthorizations = (initialAuthorizations || [])
    .filter((authorization) => isNonEmptyString(authorization?.userId) && !currentAuthorizedUserIds.includes(authorization.userId))
    .map((authorization) => authorization?.id)
    .filter(isNonEmptyString)
    .map((id) => ({ id }));

  return { createVehicleAuthorizations, deleteVehicleAuthorizations };
};

const buildAssignmentDiff = (driverAssignments: IValues["driverAssignments"]) => {
  const initialAssignments = (driverAssignments.initialAssignments || []) as IInitialAssignmentLoose[];
  const initialAssignmentsById = new Map(
    (initialAssignments || [])
      .map((x) => {
        const normalizedId = normalizeId(x?.id);
        return normalizedId ? [normalizedId, x] : null;
      })
      .filter((x): x is [string, IInitialAssignmentLoose] => Boolean(x)),
  );

  const fallbackItems = (driverAssignments as { items?: IAssignmentFormItem[] }).items || [];
  const currentAssignments = driverAssignments.assignments || fallbackItems || [];

  const createVehicleAssignments: ICreateVehicleAssignment[] = [];
  const deleteVehicleAssignments: IDeleteById[] = [];

  (currentAssignments || []).forEach((assignment) => {
    if (!assignment) {
      return;
    }

    const assignmentId = normalizeId(assignment?.id);
    const normalizedCurrent = {
      userId: assignment?.userId || assignment?.driverId,
      startDate: combineDateAndTimeToIso(assignment?.startDate, assignment?.startTime),
      endDate: combineDateAndTimeToIso(assignment?.endDate, assignment?.endTime),
    };

    const initialAssignment = assignmentId ? initialAssignmentsById.get(assignmentId) : null;

    if (!initialAssignment) {
      if (normalizedCurrent.userId) {
        createVehicleAssignments.push(normalizedCurrent as ICreateVehicleAssignment);
      }
      return;
    }

    const initialStartDateIso = normalizeIncomingAssignmentDateToIso(initialAssignment?.startDate);
    const initialEndDateIso = normalizeIncomingAssignmentDateToIso(initialAssignment?.endDate);

    const hasChanged =
      normalizedCurrent.userId !== initialAssignment?.userId ||
      normalizedCurrent.startDate !== initialStartDateIso ||
      normalizedCurrent.endDate !== initialEndDateIso;

    if (hasChanged) {
      if (assignmentId) {
        deleteVehicleAssignments.push({ id: assignmentId });
      }
      if (normalizedCurrent.userId) {
        createVehicleAssignments.push(normalizedCurrent as ICreateVehicleAssignment);
      }
    }
  });

  const currentAssignmentIds = (currentAssignments || []).map((assignment) => normalizeId(assignment?.id)).filter(isNonEmptyString);

  (initialAssignments || []).forEach((assignment) => {
    const assignmentId = normalizeId(assignment?.id);
    if (!assignmentId) {
      return;
    }

    if (!currentAssignmentIds.includes(assignmentId)) {
      deleteVehicleAssignments.push({ id: assignmentId });
    }
  });

  const uniqueDeleteVehicleAssignments = Array.from(new Set(deleteVehicleAssignments.map((x) => x.id))).map((id) => ({ id }));

  return {
    createVehicleAssignments,
    deleteVehicleAssignments: uniqueDeleteVehicleAssignments,
  };
};

const refineDataForSaving = (data: IValues): IVehicleSavePayload => {
  const driverAssignments = data.driverAssignments || {
    authorizedUsers: [],
    assignments: [],
  };

  const { createVehicleAuthorizations, deleteVehicleAuthorizations } = buildAuthorizationDiff(driverAssignments);
  const { createVehicleAssignments, deleteVehicleAssignments } = buildAssignmentDiff(driverAssignments);

  return {
    ...(data.id && { id: data.id }),
    vehicleTypeId: data.general.vehicleTypeId,
    vehicleStatusId: data.general.lifecycleStatus,
    registrationNumber: data.general.registrationNumber,
    createVehicleAuthorizations,
    deleteVehicleAuthorizations,
    createVehicleAssignments,
    deleteVehicleAssignments,
  };
};

export const getFilters = () => async (dispatch: Dispatch) => {
  try {
    const vehicleTypes: IComplexSearchFilterItem[] = await get("/api/appmasterdata/vehicleTypes/complexSearch");

    dispatch(actions.setFilter(constructInitialFilters([...(vehicleTypes || [])])));
  } catch (error) {
    // handled by empty filters fallback
  }
};

export const setFilter = (filters: IFilterParams) => defaultActions.setFilters(filters, actions);
